import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { actions } from 'darwin_core';

// The socket client is platform-agnostic (browser WebSocket global +
// aws-amplify/auth + the generated env endpoint), so it is reused in place from
// the mobile tree rather than copied. This is a hooks-based port of the mobile
// WebSocketHandler; the shared client underneath is identical.
import WebSocketClient, {
  States,
} from '../../../app/src/components/WebSocketClient.js';
import logger from '../shims/Logger.js';
import useInterval from '../hooks/useInterval.js';
import usePrevious from '../hooks/usePrevious.js';

export const WebSocketContext = createContext(null);

export function useWebSocket() {
  return useContext(WebSocketContext);
}

// Web stand-in for the mobile DeviceInfo.getReadableVersion(). Injected at build
// time from web/package.json's version by the __WEB_APP_VERSION__ define in
// web/vite.config.js, so the outbound `meta.appVersion` mirrors the app.
const appVersion = __WEB_APP_VERSION__;

// Matches the mobile keepalive cadence.
const KEEPALIVE_INTERVAL_MS = 9 * 60 * 1000;

// `authenticated` / `loaded` are injected props rather than read from an auth
// context: the web AuthenticationContext lands in a later phase. Defaults keep
// the socket closed until real auth supplies these, and keep the provider
// testable in isolation.
export default function WebSocketProvider({
  children,
  authenticated = false,
  loaded = true,
}) {
  const dispatch = useDispatch();

  // Latest auth state, read inside the async message handler and the visibility
  // listener without recreating them on every auth change. Synced each render.
  const authRef = useRef(authenticated);
  authRef.current = authenticated;

  const [webSocketStatus, setWebSocketStatus] = useState(States.Initial);
  const [retries, setRetries] = useState(0);
  const [errors, setErrors] = useState(0);
  const [ws, setWs] = useState(null);

  const prevAuthenticated = usePrevious(authenticated);

  const open = useCallback(() => {
    logger.log('Opening WS');
    const websocket = new WebSocketClient();

    setWs(websocket);

    websocket.onMessage = (e) => {
      // SECURITY: drop any frame that arrives while signed out. The socket is
      // torn down on de-auth, but a frame queued just before that (a late
      // live/hello) could otherwise run this handler after the store reset and
      // re-add device data — WEBSOCKET_LIVE_RECEIVED adds a home even when none
      // is present. Gating at the source closes that queued-frame window.
      if (!authRef.current) {
        logger.log('Dropping WebSocket frame received while signed out');
        return;
      }
      // Every inbound frame flows through darwin_core's inbound seam, which
      // classifies the message and dispatches the matching action(s). A
      // malformed or unhandled frame must not throw out of onMessage, so parse +
      // dispatch are guarded: log and drop.
      try {
        const json = JSON.parse(e.data);
        logger.log('Message received', json);
        dispatch(actions.webSocketMessageReceived(json));
      } catch (err) {
        logger.warning('Dropping malformed or unhandled WebSocket frame', err);
      }
    };

    websocket.onStateChange = (wsStatus, r, e) => {
      logger.log('WebSocket', wsStatus, { retries: r, errors: e });
      setWebSocketStatus((prevState) =>
        prevState === States.Disabled ? States.Disabled : wsStatus,
      );
      setRetries(r);
      setErrors(e);
    };

    return websocket;
  }, [dispatch]);

  // Open once authenticated. Self-cleaning: the socket this effect creates is the
  // socket this effect closes, so React's StrictMode double-invoke (and any
  // auth/unmount transition) tears down its own socket instead of orphaning one.
  useEffect(() => {
    if (loaded && authenticated) {
      const websocket = open();
      return () => {
        websocket.close();
      };
    }
    return undefined;
  }, [open, loaded, authenticated]);

  // Close on de-authentication and clear the socket state so sendWsMessage's
  // `if (ws)` guard is accurate afterwards.
  useEffect(() => {
    if (ws && !authenticated && prevAuthenticated) {
      ws.close();
      setWs(null);
      setWebSocketStatus(States.Closed);
    }
  }, [ws, authenticated, prevAuthenticated]);

  // Clean up whichever socket is current when it is replaced or on unmount. This
  // covers the imperative reopen paths (visibility / enable / resetRetries) that
  // swap `ws` outside the open-on-auth effect. Double-close is safe:
  // WebSocketClient.close() guards on this.ws.
  useEffect(() => {
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [ws]);

  // When a hidden tab returns to the foreground while the socket is still waiting
  // to reconnect, a pending retry setTimeout may have been throttled or dropped
  // while the tab was backgrounded, leaving the UI stuck "reconnecting". Force a
  // fresh open(). The Page Visibility API is the web equivalent of the mobile
  // app's AppState 'active' transition. Refs keep the listener stable while
  // reading the latest socket/status/auth.
  const wsRef = useRef(ws);
  const statusRef = useRef(webSocketStatus);
  wsRef.current = ws;
  statusRef.current = webSocketStatus;

  // Force a fresh connection attempt when connectivity may have returned. Covers
  // the reconnecting states (a retry timer the browser may have throttled while
  // backgrounded) AND Closed — once the client exhausts its retries it gives up
  // and will NOT recover on its own, so without this the user would sit on the
  // "you appear to be offline" banner until they pressed Try again. No-ops when
  // signed out, already healthy/connecting, or deliberately Disabled (so it never
  // disturbs a good socket or overrides an explicit disable). The reopen attempt
  // itself is the connectivity check: if the network is still down it just cycles
  // back to reconnecting → Closed, so triggering optimistically is safe.
  const forceReopen = useCallback(() => {
    if (!authRef.current) {
      return;
    }
    const status = statusRef.current;
    if (
      status !== States.Reconnecting &&
      status !== States.AttemptingReconnect &&
      status !== States.Closed
    ) {
      return;
    }

    logger.log('Connectivity may have returned — forcing WS reopen');

    if (wsRef.current) {
      wsRef.current.close();
    }
    setRetries(0);
    setErrors(0);
    open();
  }, [open]);

  // A backgrounded tab's retry timer can be throttled/dropped; on return to the
  // foreground, re-attempt. (The Page Visibility API is the web equivalent of the
  // mobile app's AppState 'active' transition.)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        forceReopen();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [forceReopen]);

  // Auto-recover the moment the browser regains connectivity: the `online` event
  // fires when the network interface comes back, so a socket that had given up
  // reconnects itself without the user having to press Try again.
  useEffect(() => {
    const onOnline = () => {
      logger.log('Browser reported online');
      forceReopen();
    };
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
    };
  }, [forceReopen]);

  const sendWsMessage = useCallback(
    (message) => {
      // Outbound seam. The whole build-and-send is guarded so a bad message
      // (circular ref / BigInt -> JSON.stringify throws) or a closed socket
      // (ws.send throws) can never throw out to the caller. meta is stamped last
      // so a caller cannot override appVersion.
      if (ws) {
        try {
          const args = JSON.stringify({ ...message, meta: { appVersion } });
          logger.log('WS Sending', args);
          ws.send(args);
        } catch (err) {
          logger.log('Error sending websocket message: ', err, ws.readyState);
        }
      }
    },
    [ws],
  );

  // Keepalive: ping every 9 minutes while the socket is open.
  useInterval(
    () => {
      sendWsMessage({ action: 'active' });
    },
    webSocketStatus === States.Open ? KEEPALIVE_INTERVAL_MS : null,
  );

  const enable = useCallback(() => {
    setWebSocketStatus(States.Initial);
    logger.log('Enabling websocket');
    open();
  }, [open]);

  const disable = useCallback(() => {
    setWebSocketStatus(States.Disabled);
    logger.log('Disabling websocket');
    if (ws) {
      ws.close();
    }
  }, [ws]);

  const resetRetries = useCallback(() => {
    setRetries(0);
    setErrors(0);
    setWebSocketStatus(States.AttemptingReconnect);
    open();
  }, [open]);

  // Memoized so consumers only re-render when a value they read actually
  // changes, not on every status/retries/errors tick of an unrelated field.
  const webSocketInfo = useMemo(
    () => ({
      reopen: open,
      webSocketStatus,
      retries,
      errors,
    }),
    [open, webSocketStatus, retries, errors],
  );

  const value = useMemo(
    () => ({
      sendWsMessage,
      webSocketInfo,
      disableWebsocket: disable,
      enableWebsocket: enable,
      resetRetries,
    }),
    [sendWsMessage, webSocketInfo, disable, enable, resetRetries],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}
